// frontend/src/hooks/useCreatorEarnings.ts
//
// Hook for fetching creator earnings data from the API.

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

export interface EarningsSummary {
  lifetimeEarningsCents: number;
  lifetimeEarnings: string;
  pendingEarningsCents: number;
  pendingEarnings: string;
  paidEarningsCents: number;
  paidEarnings: string;
  totalAppRuns: number;
  canRequestPayout: boolean;
}

export interface CurrentMonth {
  month: string;
  appRuns: number;
  status: string;
  note?: string;
}

export interface EarningHistory {
  id: string;
  month: string;
  appRuns: number;
  sharePercent: string;
  earningsCents: number;
  earnings: string;
  status: 'pending' | 'processing' | 'paid' | 'failed' | 'below_minimum';
  paidAt: string | null;
  periodTotalRuns: number;
  periodCreatorPool: string;
}

export interface PayoutRecord {
  id: string;
  amountCents: number;
  amount: string;
  status: 'pending' | 'processing' | 'succeeded' | 'failed';
  createdAt: string;
  processedAt: string | null;
  errorMessage: string | null;
}

export interface CreatorEarningsData {
  isConnected: boolean;
  minimumPayoutCents: number;
  minimumPayout: string;
  summary: EarningsSummary;
  currentMonth: CurrentMonth;
  history: EarningHistory[];
  payouts: PayoutRecord[];
}

interface UseCreatorEarningsReturn {
  data: CreatorEarningsData | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useCreatorEarnings(): UseCreatorEarningsReturn {
  const { session } = useAuth();
  const [data, setData] = useState<CreatorEarningsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEarnings = useCallback(async () => {
    if (!session?.access_token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-creator-earnings`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ limit: 12 }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch earnings');
      }

      const earningsData = await response.json();
      setData(earningsData);
    } catch (err) {
      console.error('Error fetching earnings:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch earnings');
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    fetchEarnings();
  }, [fetchEarnings]);

  return {
    data,
    loading,
    error,
    refetch: fetchEarnings,
  };
}

// Helper function to format month string
export function formatMonth(monthStr: string): string {
  if (!monthStr) return '';
  const [year, month] = monthStr.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

// Helper function to get status badge color
export function getStatusColor(status: string): string {
  switch (status) {
    case 'paid':
    case 'succeeded':
      return 'bg-theme-success-bg text-theme-success border-theme-success-border';
    case 'pending':
      return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
    case 'processing':
      return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
    case 'failed':
      return 'bg-theme-error-bg text-theme-error border-theme-error-border';
    case 'below_minimum':
      return 'bg-theme-surface text-theme-text-muted border-theme-border-muted';
    default:
      return 'bg-theme-surface text-theme-text-secondary border-theme-border-muted';
  }
}

// Helper function to format status for display
export function formatStatus(status: string): string {
  switch (status) {
    case 'paid':
      return 'Paid';
    case 'succeeded':
      return 'Completed';
    case 'pending':
      return 'Pending';
    case 'processing':
      return 'Processing';
    case 'failed':
      return 'Failed';
    case 'below_minimum':
      return 'Below minimum';
    case 'in_progress':
      return 'In progress';
    default:
      return status;
  }
}